import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase-config";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../assets/styles/AdminShared.css";

const initialFormData = {
  businessName: "",
  workflowGoal: "",
  currentProcess: "",
  processUsers: "",
  currentTools: "",
  customerSteps: "",
  adminSteps: "",
  dataCollected: "",
  businessRules: "",
  notifications: "",
  payments: "",
  userRoles: "",
  savedData: "",
  customerView: "",
  adminView: "",
  integrations: "",
  failureRisks: "",
  launchMustHaves: "",
  futureIdeas: "",
};

const sections = [
  {
    title: "Workflow Goal",
    fields: [
      ["businessName", "Business name", "input"],
      ["workflowGoal", "Workflow being replaced or improved", "textarea"],
      ["currentProcess", "Current process", "textarea"],
      ["processUsers", "Process users", "textarea"],
      ["currentTools", "Current tools", "textarea"],
    ],
  },
  {
    title: "Customer & Admin Flow",
    fields: [
      ["customerSteps", "Customer steps", "textarea"],
      ["adminSteps", "Admin/team steps", "textarea"],
      ["customerView", "Customer view", "textarea"],
      ["adminView", "Admin view", "textarea"],
      ["userRoles", "User roles/access", "textarea"],
    ],
  },
  {
    title: "Logic, Data & Integrations",
    fields: [
      ["dataCollected", "Data collected", "textarea"],
      ["businessRules", "Rules/conditions", "textarea"],
      ["notifications", "Notifications", "textarea"],
      ["payments", "Payments/deposits/invoices", "textarea"],
      ["savedData", "Data saved", "textarea"],
      ["integrations", "Integrations", "textarea"],
    ],
  },
  {
    title: "Scope & Risk",
    fields: [
      ["failureRisks", "Failure risks", "textarea"],
      ["launchMustHaves", "Launch must-haves", "textarea"],
      ["futureIdeas", "Future ideas", "textarea"],
    ],
  },
];

const AdminAuthorityProcessAudit = () => {
  const { projectId } = useParams();
  const [projectName, setProjectName] = useState("Loading...");
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const projectSnap = await getDoc(doc(db, "projects", projectId));
        if (!projectSnap.exists()) throw new Error("Project not found.");

        const data = projectSnap.data();
        setProjectName(data.projectName || "Unnamed Project");
        setFormData({ ...initialFormData, ...(data.authorityProcessAudit || {}) });
      } catch (err) {
        setError(err.message);
        toast.error("Failed to load process audit.");
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [projectId]);

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateDoc(doc(db, "projects", projectId), { authorityProcessAudit: formData });
      toast.success("Process audit updated.");
      setError("");
    } catch (err) {
      setError("Failed to save process audit: " + err.message);
      toast.error("Failed to save process audit.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="ap-loading"><p>Loading process audit...</p></div>;

  return (
    <div className="ap-page">
      <div className="ap-page-header">
        <a href={`/admin/project/${projectId}`} className="ap-back">← Back to project</a>
        <p className="ap-eyebrow">Admin — Authority</p>
        <h1 className="ap-title">{projectName}: Process Audit</h1>
        <p className="ap-subtitle">Review the workflow, logic, data, integrations, and risk details before scope lock.</p>
      </div>

      {error && <p className="ap-message--error" style={{ fontFamily: "var(--font-ui)", fontSize: "0.9375rem" }}>{error}</p>}

      {sections.map((section) => (
        <div key={section.title} className="ap-section" style={{ marginBottom: "20px" }}>
          <div className="ap-section-header">
            <h2 className="ap-section-title">{section.title}</h2>
          </div>
          <div className="ap-section-body">
            {section.fields.map(([name, label, type]) => (
              <div key={name} className="ap-form-group">
                <label>{label}</label>
                {type === "input" ? (
                  <input
                    type="text"
                    value={formData[name]}
                    onChange={(event) => handleChange(name, event.target.value)}
                  />
                ) : (
                  <textarea
                    value={formData[name]}
                    onChange={(event) => handleChange(name, event.target.value)}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="ap-btn-row">
        <button type="button" className="ap-btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  );
};

export default AdminAuthorityProcessAudit;
